import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';

const FloatingMenuContainer = styled.div`
  position: fixed;
  bottom: ${props => props.bottom || '20px'};
  right: ${props => props.right || '20px'};
  z-index: ${props => props.zIndex || 999};
`;

const MenuButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1000;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.6);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MenuItemsContainer = styled.div`
  position: absolute;
  bottom: 70px;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(10px)'};
  transition: all 0.3s ease;
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
`;

const MenuItem = styled.button`
  width: 50px;
  height: 50px;
  padding: 0;
  background: white;
  border: 2px solid #667eea;
  border-radius: 50%;
  color: #667eea;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  animation: ${props => props.isOpen ? 'slideUp' : 'slideDown'} 0.3s ease;
  position: relative;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 20px;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideDown {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(10px);
    }
  }

  &:hover {
    background: #667eea;
    color: white;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: 0;
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
`;

function FloatingMenu({ items, bottom, right, zIndex }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMenuItemClickEvent = (e, callback) => {
    e.stopPropagation();
    callback();
    setIsOpen(false);
  };

  const handleMenuItemMouseDown = (e) => {
    e.stopPropagation();
  };

  return (
    <FloatingMenuContainer ref={containerRef} bottom={bottom} right={right} zIndex={zIndex}>
      <MenuButton 
        onClick={() => setIsOpen(!isOpen)}
        title="Menu"
      >
        {isOpen ? <CloseIcon /> : <MenuIcon />}
      </MenuButton>

      <MenuItemsContainer isOpen={isOpen}>
        {items.map((item, index) => (
          <Tooltip key={index} title={item.label} placement="left">
            <MenuItem
              onMouseDown={handleMenuItemMouseDown}
              onClick={(e) => handleMenuItemClickEvent(e, item.action)}
              isOpen={isOpen}
            >
              {item.icon}
            </MenuItem>
          </Tooltip>
        ))}
      </MenuItemsContainer>
    </FloatingMenuContainer>
  );
}

export default FloatingMenu;
